/**
 * Project Contacts Tab — lets the team (admin + employees on the project)
 * see, call, and add contacts attached to the current project. Contacts
 * created here are also visible in the global Contacts directory.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, EnvelopeSimple, Plus, MagnifyingGlass, Trash, X } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VALID_CATEGORIES = ['client', 'fournisseur', 'collaborateur', 'entreprise'];
const CAT_COLORS = {
  client: '#1565C0',
  fournisseur: '#B45309',
  collaborateur: '#2E7D32',
  entreprise: '#6D28D9',
};
const labelize = (s) => (s || '').charAt(0).toUpperCase() + (s || '').slice(1);

// ---------------------------------------------------------------------------
// Contact creation modal
// ---------------------------------------------------------------------------
const ContactFormModal = ({ show, onClose, onSaved, projectId }) => {
  const [form, setForm] = useState({ category: 'client', name: '', company: '', job_title: '', phone: '', phone2: '', email: '', address: '', city: '', postcode: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) setForm({ category: 'client', name: '', company: '', job_title: '', phone: '', phone2: '', email: '', address: '', city: '', postcode: '', notes: '' });
  }, [show]);
  if (!show) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/contacts`, { ...form, project_id: projectId }, { withCredentials: true });
      toast.success('Contact ajoute');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur creation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="contact-form-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, width: 'min(680px, 95vw)', maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Nouveau contact</h3>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }} data-testid="contact-form-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Categorie *</label>
              <select className="form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="contact-form-category">
                {VALID_CATEGORIES.map(c => <option key={c} value={c}>{labelize(c)}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Nom *</label>
              <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom et prenom" data-testid="contact-form-name" />
            </div>
            <div>
              <label className="form-label">Societe</label>
              <input className="form-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-form-company" />
            </div>
            <div>
              <label className="form-label">Fonction / Metier</label>
              <input className="form-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Conducteur de travaux, plombier..." data-testid="contact-form-job" />
            </div>
            <div>
              <label className="form-label">Telephone *</label>
              <input className="form-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="contact-form-phone" />
            </div>
            <div>
              <label className="form-label">Telephone 2</label>
              <input className="form-input" type="tel" value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email" />
            </div>
            <div>
              <label className="form-label">Ville</label>
              <input className="form-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Adresse</label>
              <input className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Code postal</label>
              <input className="form-input" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Notes</label>
              <textarea className="form-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="contact-form-submit">{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Contact picker — pick existing contacts to attach to the project
// ---------------------------------------------------------------------------
const ContactPickerModal = ({ show, onClose, onAttached, projectId, alreadyAttachedIds }) => {
  const [all, setAll] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [filterCat, setFilterCat] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSelected(new Set());
    setSearch('');
    setLoading(true);
    axios.get(`${API_URL}/api/contacts`, { withCredentials: true })
      .then(r => setAll(r.data || []))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
  }, [show]);

  const filtered = useMemo(() => {
    const s = (search || '').toLowerCase();
    return all
      .filter(c => !alreadyAttachedIds.has(c.id))
      .filter(c => filterCat === 'all' || c.category === filterCat)
      .filter(c => !s
        || (c.name || '').toLowerCase().includes(s)
        || (c.company || '').toLowerCase().includes(s)
        || (c.phone || '').toLowerCase().includes(s)
        || (c.email || '').toLowerCase().includes(s)
        || (c.job_title || '').toLowerCase().includes(s));
  }, [all, search, filterCat, alreadyAttachedIds]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error('Selectionnez au moins un contact'); return; }
    try {
      const r = await axios.post(
        `${API_URL}/api/contacts/by-project/${projectId}/attach`,
        { contact_ids: Array.from(selected) },
        { withCredentials: true }
      );
      toast.success(`${r.data.attached || selected.size} contact(s) ajoute(s) au chantier`);
      onAttached && onAttached();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (!show) return null;
  return (
    <div data-testid="contact-picker-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, width: 'min(720px, 95vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Ajouter des contacts existants</h3>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ padding: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
            <MagnifyingGlass size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9E9EA7' }} />
            <input
              type="search"
              data-testid="contact-picker-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, societe, telephone, email..."
              className="form-input"
              style={{ paddingLeft: 32 }}
              autoFocus
            />
          </div>
          <select className="form-input" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ width: 180 }} data-testid="contact-picker-filter">
            <option value="all">Toutes categories</option>
            {VALID_CATEGORIES.map(c => <option key={c} value={c}>{labelize(c)}</option>)}
          </select>
          <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{selected.size} selectionne(s)</span>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
          {loading ? <div style={{ padding: 24, textAlign: 'center', color: '#5E5F66' }}>Chargement...</div> : null}
          {!loading && filtered.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#9E9EA7' }}>Aucun contact correspondant.</div>}
          {!loading && filtered.map(c => (
            <label
              key={c.id}
              data-testid={`contact-picker-row-${c.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid #F1F1F4', cursor: 'pointer', background: selected.has(c.id) ? '#EFF6FF' : 'transparent' }}
            >
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{c.name} {c.company ? <span style={{ color: '#5E5F66', fontWeight: 400 }}>· {c.company}</span> : null}</div>
                <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
                  {c.job_title ? `${c.job_title} · ` : ''}{c.phone}{c.email ? ` · ${c.email}` : ''}
                </div>
              </div>
              <span style={{ padding: '2px 8px', background: CAT_COLORS[c.category] || '#9E9EA7', color: 'white', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>{c.category}</span>
            </label>
          ))}
        </div>
        <div style={{ padding: 16, borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={selected.size === 0} data-testid="contact-picker-attach">Ajouter au chantier ({selected.size})</button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main tab component
// ---------------------------------------------------------------------------
export const ProjectContactsTab = ({ project, currentUser }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const projectId = project?.id;
  const isAdmin = (currentUser?.role || '').toLowerCase() === 'admin';

  const fetchContacts = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/contacts/by-project/${projectId}`, { withCredentials: true })
      .then(r => setContacts(r.data || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const attachedIds = useMemo(() => new Set(contacts.map(c => c.id)), [contacts]);

  const filtered = useMemo(() => {
    const s = (search || '').toLowerCase();
    return contacts
      .filter(c => filterCat === 'all' || c.category === filterCat)
      .filter(c => !s
        || (c.name || '').toLowerCase().includes(s)
        || (c.company || '').toLowerCase().includes(s)
        || (c.phone || '').toLowerCase().includes(s)
        || (c.email || '').toLowerCase().includes(s)
        || (c.job_title || '').toLowerCase().includes(s));
  }, [contacts, search, filterCat]);

  const detach = async (cid) => {
    if (!window.confirm('Retirer ce contact du chantier ? (Le contact reste dans l\'annuaire global)')) return;
    try {
      await axios.post(`${API_URL}/api/contacts/by-project/${projectId}/detach`, { contact_id: cid }, { withCredentials: true });
      toast.success('Contact retire du chantier');
      fetchContacts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid="project-contacts-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Contacts du chantier</h3>
          <span style={{ padding: '2px 10px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600 }} data-testid="project-contacts-count">{contacts.length}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={() => setShowPicker(true)} data-testid="contacts-search-existing-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MagnifyingGlass size={16} /> Rechercher des contacts
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)} data-testid="contacts-add-new-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> Ajouter un contact
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          type="search"
          data-testid="project-contacts-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher dans les contacts du chantier..."
          className="form-input"
          style={{ flex: 1, minWidth: 240 }}
        />
        <select className="form-input" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ width: 200 }}>
          <option value="all">Toutes categories</option>
          {VALID_CATEGORIES.map(c => <option key={c} value={c}>{labelize(c)}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#5E5F66' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div data-testid="project-contacts-empty" style={{ padding: 40, textAlign: 'center', color: '#9E9EA7', background: '#F9FAFB', borderRadius: 8, border: '1px dashed #E2E2E6' }}>
          {contacts.length === 0
            ? "Aucun contact pour le moment. Utilisez les boutons en haut pour en ajouter."
            : "Aucun contact ne correspond a votre recherche."}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 12 }}>
          {filtered.map(c => (
            <div key={c.id} data-testid={`project-contact-${c.id}`} className="card" style={{ padding: 14, border: '1px solid #E2E2E6', borderRadius: 8, background: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{c.name}</div>
                  {c.company && <div style={{ color: '#5E5F66', fontSize: '0.82rem' }}>{c.company}</div>}
                  {c.job_title && <div style={{ color: '#5E5F66', fontSize: '0.78rem', fontStyle: 'italic' }}>{c.job_title}</div>}
                </div>
                <span style={{ padding: '2px 8px', background: CAT_COLORS[c.category] || '#9E9EA7', color: 'white', borderRadius: 4, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase' }}>{c.category}</span>
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {c.phone && (
                  <a href={`tel:${c.phone.replace(/\s/g, '')}`} data-testid={`call-${c.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#16A34A', color: 'white', textDecoration: 'none', borderRadius: 6, fontSize: '0.85rem', fontWeight: 600, justifyContent: 'center' }}>
                    <Phone size={16} weight="fill" /> Appeler {c.phone}
                  </a>
                )}
                {c.phone2 && (
                  <a href={`tel:${c.phone2.replace(/\s/g, '')}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#2563EB', color: 'white', textDecoration: 'none', borderRadius: 6, fontSize: '0.82rem', justifyContent: 'center' }}>
                    <Phone size={14} /> {c.phone2}
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#F4F5F7', color: '#1F2937', textDecoration: 'none', borderRadius: 6, fontSize: '0.8rem', justifyContent: 'center' }}>
                    <EnvelopeSimple size={14} /> {c.email}
                  </a>
                )}
              </div>
              {(c.address || c.city) && (
                <div style={{ marginTop: 8, fontSize: '0.76rem', color: '#5E5F66' }}>
                  {c.address && <div>{c.address}</div>}
                  {(c.postcode || c.city) && <div>{c.postcode} {c.city}</div>}
                </div>
              )}
              {c.notes && <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#5E5F66', fontStyle: 'italic', padding: 8, background: '#FFFBEB', borderRadius: 4 }}>{c.notes}</div>}
              {isAdmin && (
                <button type="button" onClick={() => detach(c.id)} data-testid={`detach-${c.id}`}
                  style={{ marginTop: 10, background: 'transparent', border: '1px solid #E5E7EB', color: '#9E9EA7', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: '0.74rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Trash size={12} /> Retirer du chantier
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <ContactFormModal show={showForm} onClose={() => setShowForm(false)} onSaved={fetchContacts} projectId={projectId} />
      <ContactPickerModal show={showPicker} onClose={() => setShowPicker(false)} onAttached={fetchContacts} projectId={projectId} alreadyAttachedIds={attachedIds} />
    </div>
  );
};

export default ProjectContactsTab;
